import { CabinNavigation, Tour } from "../interfaces/itinerary.interface";

export interface CruiseCabinPax {
  adult: number;
  children: number;
}

export const findCabinDetail = (cabinNavigation: CabinNavigation | undefined, tourId: number): Tour | undefined => {
  for (const departure of cabinNavigation?.departures || []) {
    for (const roomType of departure.room_types || []) {
      const cabin = roomType.cabins.find((c) => c.tour_id === tourId);
      if (cabin?.detail) return cabin.detail;
    }
  }
  return undefined;
};

export const CRUISE_ADULT_CATEGORY = "adult";
export const CRUISE_CHILD_CATEGORY = "child_with_bed";

export const cabinPaxTotal = (cabin: CruiseCabinPax): number => cabin.adult + cabin.children;

export const totalCruisePax = (cabins: CruiseCabinPax[]): number =>
  cabins.reduce((sum, cabin) => sum + cabinPaxTotal(cabin), 0);

export const newCabin = (multiplier: number): CruiseCabinPax => ({ adult: multiplier, children: 0 });

export const addCabin = (cabins: CruiseCabinPax[], multiplier: number, maxCabins: number): CruiseCabinPax[] =>
  cabins.length >= maxCabins ? cabins : [...cabins, newCabin(multiplier)];

export const removeCabin = (cabins: CruiseCabinPax[]): CruiseCabinPax[] =>
  cabins.length <= 1 ? cabins : cabins.slice(0, -1);

export const changeCabinGuest = (
  cabins: CruiseCabinPax[],
  index: number,
  key: keyof CruiseCabinPax,
  delta: number,
  multiplier: number
): CruiseCabinPax[] =>
  cabins.map((cabin, i) => {
    if (i !== index) return cabin;
    if (delta > 0 && cabinPaxTotal(cabin) >= multiplier) return cabin;
    const next = cabin[key] + delta;
    if (next < 0) return cabin;
    return { ...cabin, [key]: next };
  });

export const maxCabinsFor = (maxBookingSeats: number, multiplier?: number): number =>
  Math.max(1, Math.floor(maxBookingSeats / (multiplier || 1)));

export const flattenCabinsToTravelers = (cabins: CruiseCabinPax[]): string[] => {
  const travelers: string[] = [];
  cabins.forEach((cabin) => {
    for (let i = 0; i < cabin.adult; i++) travelers.push(CRUISE_ADULT_CATEGORY);
    for (let i = 0; i < cabin.children; i++) travelers.push(CRUISE_CHILD_CATEGORY);
  });
  return travelers;
};

export const travelerCountsFrom = (travelers: string[]): Record<string, number> =>
  travelers.reduce((acc, key) => {
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

export const toCabinGroups = (cabins: CruiseCabinPax[]): number[] => cabins.map(cabinPaxTotal);

export interface CabinMemberMeta {
  cabinNo: number;
  localIndex: number;
  isStart: boolean;
}

export const cabinMetaFromGroups = (cabinGroups?: number[]): CabinMemberMeta[] | null => {
  if (!cabinGroups || cabinGroups.length === 0) return null;
  const meta: CabinMemberMeta[] = [];
  cabinGroups.forEach((size, cabinIndex) => {
    for (let local = 0; local < size; local++) {
      meta.push({ cabinNo: cabinIndex + 1, localIndex: local, isStart: local === 0 });
    }
  });
  return meta;
};

export const cruisePaxSummary = (cabins: CruiseCabinPax[]): string => {
  const cabinCount = cabins.length;
  const adults = cabins.reduce((sum, c) => sum + c.adult, 0);
  const children = cabins.reduce((sum, c) => sum + c.children, 0);

  const parts = [`${adults} ${adults === 1 ? "Adult" : "Adults"}`];
  if (children > 0) parts.push(`${children} ${children === 1 ? "Child" : "Children"}`);

  return `${cabinCount} ${cabinCount === 1 ? "Cabin" : "Cabins"} (${parts.join(", ")})`;
};
